import { useState, useEffect } from 'react';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import apiClient from '@/lib/axios';
import type { ApiResponse } from '@/types';

interface Estimator {
  id: number;
  firstName: string;
  lastName: string;
}

interface EstimatorSelectorProps {
  selectedUserId: number | null;
  onSelect: (userId: number | null) => void;
}

/**
 * Dropdown selector for choosing an estimator to view their account
 * Only shown to admins
 */
export function EstimatorSelector({ selectedUserId, onSelect }: EstimatorSelectorProps) {
  const [estimators, setEstimators] = useState<Estimator[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEstimators = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get<ApiResponse<Estimator[]>>('/users', {
          params: {
            role: 'Estimator',
            isActive: true,
          },
        });
        const sorted = [...response.data.data].sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`;
          const nameB = `${b.firstName} ${b.lastName}`;
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        });
        setEstimators(sorted);
      } catch (error) {
        console.error('Failed to fetch estimators:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEstimators();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onSelect(value ? parseInt(value) : null);
  };

  return (
    <div className="max-w-sm">
      <Label htmlFor="estimator">Estimator</Label>
      <Select
        id="estimator"
        value={selectedUserId?.toString() ?? ''}
        onChange={handleChange}
        disabled={isLoading}
      >
        <option value="">Select an estimator...</option>
        {estimators.map((est) => (
          <option key={est.id} value={est.id.toString()}>
            {est.firstName} {est.lastName}
          </option>
        ))}
      </Select>
    </div>
  );
}
